import { Button } from "@/components/ui/button";
import { Markdown } from "@/components/ui/markdown";
import { WikiSidebar } from "@/components/wiki/wiki-sidebar";
import { api } from "@/lib/api";
import { preprocessWikiMarkdown } from "@/lib/wiki-markdown";
import { Link, createFileRoute, useLoaderData } from "@tanstack/react-router";

/**
 * `/projects/:slug/wiki` — the wiki home / index, rendered inside the
 * project layout. Hub owns the content (`WikiPage`, kept warm by the
 * periodic GitLab sync) — this reads Hub's own copy, never GitLab live.
 * All navigation (page links, New / Edit) is soft-routed within the
 * SPA.
 */

type WikiPageSummary = { slug: string; title: string };

type WikiHome = { slug: string; title: string; content: string };

type WikiResponse = {
  pages: WikiPageSummary[];
  home: WikiHome | null;
  sidebarMarkdown: string | null;
  canEdit: boolean;
};

export const Route = createFileRoute("/_app/projects/$slug/wiki/")({
  loader: ({ params }) => api.get<WikiResponse>(`/api/projects/${params.slug}/wiki`),
  component: ProjectWikiPage,
});

function ProjectWikiPage() {
  const { pages, home, sidebarMarkdown, canEdit } = Route.useLoaderData();
  const { project } = useLoaderData({ from: "/_app/projects/$slug" });
  const { slug } = Route.useParams();

  return (
    <div className="flex gap-6">
      <WikiSidebar slug={slug} pages={pages} sidebarMarkdown={sidebarMarkdown} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">{home ? home.title : "Wiki"}</h2>
          <div className="flex items-center gap-2">
            {canEdit && home && (
              <Button variant="outline" size="sm" asChild>
                <Link
                  to="/projects/$slug/wiki/$wikiSlug/edit"
                  params={{ slug, wikiSlug: home.slug }}
                >
                  Edit
                </Link>
              </Button>
            )}
            {canEdit && (
              <Button size="sm" asChild>
                <Link to="/projects/$slug/wiki/new" params={{ slug }}>
                  New Page
                </Link>
              </Button>
            )}
          </div>
        </div>

        {home ? (
          <div className="rounded-md border bg-card p-6">
            <Markdown
              content={preprocessWikiMarkdown(home.content, `/projects/${slug}/wiki`)}
              gitlabUploadProjectId={project.id}
            />
          </div>
        ) : pages.length > 0 ? (
          <div className="rounded-md border bg-card divide-y">
            {pages.map((p) => (
              <div key={p.slug} className="flex items-center justify-between px-4 py-3">
                <Link
                  to="/projects/$slug/wiki/$wikiSlug"
                  params={{ slug, wikiSlug: p.slug }}
                  className="font-medium hover:underline text-primary"
                >
                  {p.title}
                </Link>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-md border bg-card p-12 text-center">
            <p className="text-muted-foreground mb-4">No wiki pages yet.</p>
            {canEdit && (
              <Button asChild>
                <Link to="/projects/$slug/wiki/new" params={{ slug }}>
                  Create the first page
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
